import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, Phone } from "lucide-react";
import { getMenArticle, MEN_ARTICLES } from "@/data/learn/men-and-trauma";
import { MenExercises, MenCrisisNote } from "@/components/men/MenExercises";
import { TOOL_ARTICLE_SLUGS } from "@/data/learn/men-tools";

export const Route = createFileRoute("/learn/men-and-trauma/$slug")({
  loader: ({ params }) => {
    const article = getMenArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    const a = loaderData.article;
    const title = `${a.title} — Men & Trauma | Recovery Trauma™`;
    return {
      meta: [
        { title },
        { name: "description", content: a.short },
        { property: "og:title", content: title },
        { property: "og:description", content: a.short },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: `https://recoverytrauma.com/learn/men-and-trauma/${a.slug}` }],
    };
  },
  notFoundComponent: MenNotFound,
  component: MenArticlePage,
});

function MenNotFound() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <p className="text-foreground">This article couldn't be found.</p>
      <Link to="/learn/men-and-trauma" className="mt-4 inline-block text-primary underline">Back to Men & Trauma</Link>
    </main>
  );
}

function MenArticlePage() {
  const { article: a } = Route.useLoaderData();
  const idx = MEN_ARTICLES.findIndex((x) => x.slug === a.slug);
  const next = MEN_ARTICLES[(idx + 1) % MEN_ARTICLES.length];
  return (
    <main className="mx-auto max-w-3xl px-4 pb-16 pt-6 sm:pt-10">
      <Link to="/learn/men-and-trauma" className="inline-flex min-h-[44px] items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> Men & Trauma
      </Link>
      <h1 className="mt-2 font-serif text-3xl text-foreground">{a.title}</h1>

      {a.safety === "crisis" && (
        <div className="mt-4 flex gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground">
          <Phone className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p>
            If you're in immediate danger call 999 (UK). Samaritans are free 24/7 on 116 123.{" "}
            <Link to="/crisis" className="font-medium underline">Crisis support</Link>
          </p>
        </div>
      )}

      <div className="mt-4 space-y-3 text-foreground/90">
        {a.intro.map((p) => <p key={p}>{p}</p>)}
      </div>

      <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
        {a.sections.map((s, i) => (
          <details key={s.heading} className="group px-4" open={i === 0}>
            <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 py-3 font-medium text-foreground">
              {s.heading}
              <ChevronDown className="h-4 w-4 shrink-0 text-primary transition-transform group-open:rotate-180" />
            </summary>
            <div className="pb-4 text-sm text-foreground/85">
              {s.body && <p>{s.body}</p>}
              {s.bullets && (
                <ul className="mt-2 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="relative ps-5 before:absolute before:start-0 before:text-primary before:content-['•']">{b}</li>
                  ))}
                </ul>
              )}
              {s.cards && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {s.cards.map((c) => (
                    <div key={c} className="rounded-xl border border-border bg-muted/40 p-3 text-sm font-medium text-foreground">{c}</div>
                  ))}
                </div>
              )}
              {s.afterCards && <p className="mt-3 leading-relaxed">{s.afterCards}</p>}
            </div>
          </details>
        ))}
      </div>

      {a.note && <p className="mt-4 rounded-xl bg-muted/50 p-4 text-sm italic text-muted-foreground">{a.note}</p>}

      {TOOL_ARTICLE_SLUGS.has(a.slug) && (
        <Link
          to="/learn/men-and-trauma/tools"
          className="mt-6 flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4 transition-colors hover:bg-primary/15"
        >
          <span className="text-2xl">🧰</span>
          <span className="min-w-0 flex-1">
            <span className="block font-medium text-foreground">Feeling on edge or overwhelmed? Try a Tool</span>
            <span className="block text-sm text-muted-foreground">Tool Shop grounding, cool water or a wall push — free, quick and physical.</span>
          </span>
          <span className="text-sm font-medium text-primary">Try →</span>
        </Link>
      )}

      <MenExercises keys={a.exercises} />

      <Link
        to="/learn/men-and-trauma/$slug"
        params={{ slug: next.slug }}
        className="mt-6 block rounded-xl border border-border p-4 hover:border-primary"
      >
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Next</span>
        <span className="block font-medium text-foreground">{next.title}</span>
      </Link>
      <MenCrisisNote />
    </main>
  );
}
